import { Geolocation } from '@capacitor/geolocation';

const STAYS_KEY = 'indexcasePrivateStayEvents';
export const STAY_PERSISTED_EVENT =
  'indexcase-private-stay-persisted';

const MIN_STAY_MS = 10 * 60 * 1000;
const MAX_DISTANCE_METERS = 50;
let watchId = null;
let candidate = null;

function radians(value) { return value * Math.PI / 180; }
function distanceMeters(a, b) {
  const R = 6371000;
  const dLat = radians(b.latitude - a.latitude);
  const dLon = radians(b.longitude - a.longitude);
  const lat1 = radians(a.latitude);
  const lat2 = radians(b.latitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function getPrivateStayEvents() {
  try { return JSON.parse(localStorage.getItem(STAYS_KEY)) || []; }
  catch (e) { return []; }
}


function storePrivateStayEvents(stays) {
  localStorage.setItem(
    STAYS_KEY,
    JSON.stringify((stays || []).slice(-500))
  );
  return stays || [];
}


export function addManualStayEvent({
  latitude,
  longitude,
  accuracy = null,
  startedAt,
  endedAt
}) {
  const lat = Number(latitude);
  const lng = Number(longitude);

  const start = new Date(startedAt);
  const end = new Date(endedAt);

  if (
    !Number.isFinite(lat) ||
    lat < -90 ||
    lat > 90 ||
    !Number.isFinite(lng) ||
    lng < -180 ||
    lng > 180
  ) {
    throw new Error(
      'Choose a valid location.'
    );
  }

  if (
    !Number.isFinite(start.getTime()) ||
    !Number.isFinite(end.getTime()) ||
    end <= start
  ) {
    throw new Error(
      'Enter a valid arrival and departure time.'
    );
  }

  if (
    end.getTime() >
    Date.now() + 60 * 1000
  ) {
    throw new Error(
      'Departure time cannot be in the future.'
    );
  }

  const parsedAccuracy =
    accuracy === null ||
    accuracy === undefined
      ? null
      : Number(accuracy);

  const stay = {
    id:
      `manual-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`,
    latitude: lat,
    longitude: lng,
    accuracy:
      Number.isFinite(parsedAccuracy)
        ? Math.max(0, parsedAccuracy)
        : null,
    startedAt:
      start.toISOString(),
    endedAt:
      end.toISOString(),
    source:
      'manual_map',
    uploaded:
      false
  };

  return {
    stay,
    stays:
      storePrivateStayEvents([
        ...getPrivateStayEvents(),
        stay
      ])
  };
}

export function prunePrivateStayEvents(retainHours = 24) {
  const hours = Number(retainHours);

  const boundedHours =
    Number.isFinite(hours) && hours > 0
      ? Math.min(hours, 24 * 90)
      : 24;

  const cutoff =
    Date.now() - boundedHours * 60 * 60 * 1000;

  const kept = getPrivateStayEvents().filter(stay => {
    const ended = new Date(stay.endedAt).getTime();
    return Number.isFinite(ended) && ended > cutoff;
  });

  storePrivateStayEvents(kept);
  return kept;
}

export function markPrivateStayEventsUploaded(ids) {
  const accepted = new Set(
    (ids || []).map(String)
  );

  if (!accepted.size) {
    return getPrivateStayEvents();
  }

  const next = getPrivateStayEvents().map(stay =>
    accepted.has(String(stay.id))
      ? {
          ...stay,
          uploaded: true,
          uploadedAt: new Date().toISOString()
        }
      : stay
  );

  storePrivateStayEvents(next);
  return next;
}

function persistCandidate(endAt) {
  if (!candidate || endAt - candidate.startedAt < MIN_STAY_MS) return;
  const stays = getPrivateStayEvents();

  const stay = {
    id: `stay-${candidate.startedAt}`,
    latitude: candidate.latitude,
    longitude: candidate.longitude,
    accuracy:
      Number.isFinite(candidate.accuracy)
        ? candidate.accuracy
        : null,
    startedAt: new Date(candidate.startedAt).toISOString(),
    endedAt: new Date(endAt).toISOString(),
    source: 'automatic_foreground',
    uploaded: false,
  };

  stays.push(stay);
  localStorage.setItem(
    STAYS_KEY,
    JSON.stringify(stays.slice(-500))
  );

  if (
    typeof window !== 'undefined' &&
    typeof window.dispatchEvent === 'function'
  ) {
    window.dispatchEvent(
      new CustomEvent(
        STAY_PERSISTED_EVENT,
        { detail: { id: stay.id } }
      )
    );
  }
}

function processPosition(position) {
  if (!position || !position.coords) return;
  const accuracy = Number(position.coords.accuracy);

  const point = {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy:
      Number.isFinite(accuracy) && accuracy >= 0
        ? accuracy
        : null
  };

  const now = position.timestamp || Date.now();
  if (!candidate) {
    candidate = { ...point, startedAt: now, lastSeenAt: now };
    return;
  }
  if (distanceMeters(candidate, point) <= MAX_DISTANCE_METERS) {
    candidate.lastSeenAt = now;

    // Keep the most conservative reported uncertainty observed
    // during this candidate stay.
    if (Number.isFinite(point.accuracy)) {
      candidate.accuracy =
        Number.isFinite(candidate.accuracy)
          ? Math.max(candidate.accuracy, point.accuracy)
          : point.accuracy;
    }

    return;
  }
  persistCandidate(candidate.lastSeenAt);
  candidate = { ...point, startedAt: now, lastSeenAt: now };
}

export async function getCurrentPositionForManualStay() {
  const permission =
    await Geolocation.requestPermissions();

  if (
    permission.location !== 'granted' &&
    permission.coarseLocation !== 'granted'
  ) {
    throw new Error(
      'Location permission was not granted.'
    );
  }

  const position =
    await Geolocation.getCurrentPosition({
      enableHighAccuracy: false,
      timeout: 30000,
      maximumAge: 60000
    });

  if (
    !position ||
    !position.coords
  ) {
    throw new Error(
      'Your current location could not be determined.'
    );
  }

  return {
    latitude:
      Number(position.coords.latitude),
    longitude:
      Number(position.coords.longitude),
    accuracy:
      Number.isFinite(
        Number(position.coords.accuracy)
      )
        ? Number(position.coords.accuracy)
        : null
  };
}

export async function startForegroundStayDetection() {
  if (watchId !== null) return watchId;
  const permission = await Geolocation.requestPermissions();
  if (permission.location !== 'granted' && permission.coarseLocation !== 'granted') {
    throw new Error('Location permission was not granted.');
  }
  watchId = await Geolocation.watchPosition({ enableHighAccuracy: false, timeout: 30000 }, (position, error) => {
    if (error) return console.warn('IndexCase location watch:', error);
    processPosition(position);
  });
  return watchId;
}

export async function stopForegroundStayDetection() {
  if (watchId !== null) await Geolocation.clearWatch({ id: watchId });
  if (candidate) persistCandidate(candidate.lastSeenAt || Date.now());
  watchId = null;
  candidate = null;
}
